import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, styled, Switch } from '@mui/material'

import EditIcon from '@mui/icons-material/Edit'
import { DataGrid, GridActionsCellItem, GridToolbar, useGridApiRef } from '@mui/x-data-grid'
import { ConfirmationDialog } from 'components'
import { useSnackbar } from 'notistack'
import { disableUser, enableUser } from 'pages/admin/common/adminDatastore'

const StyledDataGrip = styled(DataGrid)(() => [
  {
    '& .MuiDataGrid-virtualScrollerContent': {
      'flex-basis': '0 !important'
    }
  }
])

export const UsersTable = ({ data, setUser, isLoading }) => {
  const [rows, setRows] = useState([...(data ?? [])])

  useEffect(() => {
    if (!data) {
      return
    }
    setRows([...data])
  }, [data])

  const [rowModesModel, setRowModesModel] = useState({})
  const [userToDelete, setUserToDelete] = useState(undefined)

  const { enqueueSnackbar } = useSnackbar()
  const apiRef = useGridApiRef()
  const COLUMNS = [
    {
      field: 'FirstName',
      flex: 1,
      headerName: 'First Name',
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'LastName',
      flex: 1,
      headerName: 'Last Name',
      disableColumnMenu: true,
      editable: true
    },
    {
      field: 'Email',
      flex: 1,
      headerName: 'Email address',
      disableColumnMenu: true,
      editable: false
    },
    {
      field: 'CompanyNames',
      headerName: 'Company Name',
      flex: 1,
      hideable: false,
      sortable: false,
      disableColumnMenu: true
    },

    {
      field: 'Roles',
      flex: 1,
      disableColumnMenu: true,
      editable: true,
      headerName: 'Roles'
    },
    {
      field: 'Enabled',
      type: 'actions',
      flex: 1,
      disableColumnMenu: true,
      editable: true,
      headerName: 'Status',
      cellClassName: 'actions',
      getActions: ({ id, row }) => {
        const name = `${row.FirstName} ${row.LastName}`
        const isLoading = rowModesModel[id]?.mode === 'status_loading'

        if (isLoading) {
          return [
            <Box
              key="loading"
              sx={{
                width: '14px'
              }}>
              <CircularProgress
                sx={{
                  width: '14px'
                }}
                size="small"
              />
            </Box>
          ]
        }
        return [
          <Switch
            size="small"
            color={row.Enabled ? 'secondary' : 'default'}
            key={id}
            checked={row.Enabled}
            onChange={async (event) => {
              setRowModesModel({ ...rowModesModel, [id]: { mode: 'status_loading' } })
              if (event.target.checked) {
                await handleUserEnabled(id, name)
              } else {
                await handleUserDisabled(id, name)
              }
              setRowModesModel({ ...rowModesModel, [id]: { mode: undefined } })
            }}
          />
        ]
      }
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 100,
      cellClassName: 'actions',
      getActions: ({ row }) => {
        return [
          <GridActionsCellItem
            key="edit"
            icon={<EditIcon />}
            label="Edit"
            onClick={handleEditClick(row)}
            color="inherit"
          />
        ]
      }
    }
  ]

  const handleEditClick = (row) => () => {
    setUser(row)
  }

  const updateUserStatus = (index, status) => {
    setRows((rows) => {
      const rows_ = [...rows]
      rows_.splice(index, 1, {
        ...rows[index],
        Enabled: status
      })

      return rows_
    })
  }

  const handleUserDisabled = async (id, name) => {
    enqueueSnackbar(`Disabling ${name}`)
    const index = rows.findIndex((row) => {
      return row.Username === id
    })
    updateUserStatus(index, false)

    try {
      await disableUser(id)
      enqueueSnackbar(`${name} was successfully disabled`)
    } catch {
      updateUserStatus(index, true)

      enqueueSnackbar(`An error occurred while disabling ${name}`, {
        variant: 'error'
      })
    }
  }

  const handleUserEnabled = async (id, name) => {
    enqueueSnackbar(`Enabling ${name}`)
    const index = rows.findIndex((row) => {
      return row.Username === id
    })

    updateUserStatus(index, true)
    try {
      await enableUser(id)
      enqueueSnackbar(`${name} was successfully enabled`)
    } catch {
      updateUserStatus(index, false)
      enqueueSnackbar(`An error occurred while enabling ${name}`, {
        variant: 'error'
      })
    }
  }

  // const handleRowEditStop = (params, event) => {
  //   if (params.reason === GridRowEditStopReasons.rowFocusOut) {
  //     event.defaultMuiPrevented = true
  //   }
  // }

  // const handleDeleteClick = (id, name) => () => {
  //   setRowModesModel({ ...rowModesModel, [id]: { mode: 'delete_loading' } })
  //   setUserToDelete({ id, name })
  // }

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel)
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="calc(100% - 225px)"
      sx={{
        marginBottom: '4em !important',
        overflow: 'hidden'
      }}>
      <StyledDataGrip
        apiRef={apiRef}
        loading={isLoading}
        showCellVerticalBorder={false}
        getRowId={(data) => {
          return data.Username
        }}
        disableMultipleRowSelection
        onRowModesModelChange={handleRowModesModelChange}
        rows={rows}
        editMode="row"
        columns={COLUMNS}
        slots={{ toolbar: GridToolbar }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            csvOptions: {
              allColumns: true
            }
          },
          loadingOverlay: {
            variant: 'skeleton',
            noRowsVariant: 'skeleton'
          }
        }}
      />
      {userToDelete && (
        <ConfirmationDialog
          saveButtonProps={{
            color: 'error'
          }}
          text={`Are you sure you want to delete '${userToDelete.name}'?`}
          onClose={(result) => {
            if (result === true) {
              setRowModesModel((rowModesModel) => {
                return {
                  ...rowModesModel,
                  [userToDelete.id]: {
                    mode: 'loading'
                  }
                }
              })

              //setRows(rows.filter((row) => row.MaterialNumber !== materialToDelete))
              enqueueSnackbar({
                message: `User '${userToDelete}' has been deleted`
              })
            }
            setUserToDelete(undefined)
          }}
        />
      )}
    </Box>
  )
}
